import { useQuery } from '@tanstack/react-query';
import { Building2, Mail, ShieldCheck, User as UserIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import { useAppSelector } from '../../redux/store';
import { machinesApi } from '../../services/machinesApi';

const ProfileView = () => {
  const user = useAppSelector((s) => s.auth.user);
  const machinesQuery = useQuery({
    queryKey: ['machines'],
    queryFn: async () => (await machinesApi.list()).data,
  });

  if (!user) return null;
  const fullName = [user.firstname, user.lastname].filter(Boolean).join(' ') || user.username || '—';

  return (
    <>
      <PageHeader title="Profile" subtitle="Your account and access details." />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-1">
          <div className="flex flex-col items-center gap-4 py-2 text-center">
            <div className="flex size-20 items-center justify-center rounded-full bg-tfs-primary text-2xl font-semibold text-white">
              {(user.firstname?.[0] ?? user.username?.[0] ?? '?').toUpperCase()}
            </div>
            <div>
              <p className="text-lg font-semibold text-slate-900">{fullName}</p>
              <p className="text-sm text-slate-500">@{user.username}</p>
            </div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {user.tfstaff && (
                <span className="rounded-full bg-tfs-accent/15 px-2.5 py-0.5 text-xs font-medium text-tfs-primary">
                  TFS staff
                </span>
              )}
              {user.roles.map((r) => (
                <span
                  key={r}
                  className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700"
                >
                  {r}
                </span>
              ))}
            </div>
          </div>
        </Card>

        <Card className="xl:col-span-2" title="Account">
          <dl className="grid grid-cols-1 gap-y-4 sm:grid-cols-2">
            <Row Icon={UserIcon} label="UID" value={String(user.uid)} mono />
            <Row Icon={UserIcon} label="Username" value={user.username ?? '—'} />
            <Row Icon={Mail} label="Email" value={user.email ?? '—'} />
            <Row
              Icon={ShieldCheck}
              label="Verified"
              value={user.verified === 1 ? 'Yes' : user.verified === 2 ? 'Banned' : 'Pending'}
            />
            <Row
              Icon={ShieldCheck}
              label="Access level"
              value={String(user.accessLevel)}
              mono
            />
            <Row
              Icon={Building2}
              label="Subscribed machines"
              value={String(machinesQuery.data?.length ?? '—')}
            />
          </dl>
        </Card>

        <Card className="xl:col-span-3" title="Permissions" subtitle="Module + action grants from your roles.">
          <ul className="flex flex-wrap gap-1.5">
            {user.permissions.length === 0 && (
              <li className="text-sm text-slate-400">No permissions assigned.</li>
            )}
            {user.permissions.map((p) => (
              <li
                key={`${p.module}:${p.action}`}
                className="rounded-full bg-tfs-accent/10 px-2.5 py-1 text-xs font-medium text-tfs-primary"
              >
                {p.module}:{p.action}
              </li>
            ))}
          </ul>
        </Card>

        <Card className="xl:col-span-3" title="Subscribed machines">
          {machinesQuery.data && machinesQuery.data.length > 0 ? (
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {machinesQuery.data.map((m) => (
                <li
                  key={m.uid}
                  className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2.5"
                >
                  <div className="flex flex-col">
                    <span className="font-mono text-sm font-medium text-slate-800">{m.id}</span>
                    <span className="text-xs text-slate-500">
                      {m.machineType} · {m.location?.name ?? 'no location'}
                    </span>
                  </div>
                  {m.location?.timezone && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                      {m.location.timezone}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">No subscriptions.</p>
          )}
        </Card>
      </div>
    </>
  );
};

const Row = ({
  Icon,
  label,
  value,
  mono,
}: {
  Icon: typeof UserIcon;
  label: string;
  value: string;
  mono?: boolean;
}) => (
  <div className="flex items-start gap-3">
    <Icon className="mt-0.5 size-4 shrink-0 text-slate-400" aria-hidden />
    <div className="flex min-w-0 flex-1 flex-col leading-tight">
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd
        className={`mt-0.5 truncate text-sm text-slate-800 ${mono ? 'font-mono text-xs' : ''}`}
      >
        {value}
      </dd>
    </div>
  </div>
);

export default ProfileView;
